import { TextField, TextFieldProps } from '@mui/material';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

type DebouncedTextFieldProps = Omit<TextFieldProps, 'onChange' | 'value'> & {
  value: string;
  onChange: (value: string) => void;
  debounceMs?: number;
};

/**
 * TextField that shows keystrokes instantly via local state and only calls
 * onChange after the user pauses typing, preventing per-keystroke parent
 * re-renders that cause visible input lag.
 * @example <DebouncedTextField value={x} onChange={v => setX(v)} />
 */
export function DebouncedTextField({
  value: externalValue,
  onChange,
  debounceMs = 300,
  ...props
}: DebouncedTextFieldProps) {
  const [localValue, setLocalValue] = useState(externalValue);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const isEditing = useRef(false);

  // Sync from parent (hydration / form reset) but not while user is typing.
  useEffect(() => {
    if (!isEditing.current) {
      setLocalValue(externalValue);
    }
  }, [externalValue]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const val = e.target.value;
    isEditing.current = true;
    setLocalValue(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      isEditing.current = false;
      onChange(val);
    }, debounceMs);
  };

  // Clear pending timer on unmount.
  useEffect(() => () => clearTimeout(timerRef.current), []);

  return <TextField {...props} value={localValue} onChange={handleChange} />;
}

export default DebouncedTextField;
